import { ErrorState } from "@/shared/components/ErrorState";
import { formatDateTime, formatRelative } from "@/shared/lib/datetime";
import { type TempAccessGrant, TempAccessStatus } from "@keystone/contracts/temp-access";
import { ActionIcon, Badge, Card, Group, HoverCard, Text, Tooltip } from "@mantine/core";
import { DataTable, type DataTableSortStatus } from "mantine-datatable";
import { type ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiMessageSquare } from "react-icons/fi";

const PAGE_SIZE_OPTIONS = [15, 25, 50];

const STATUS_COLOR: Record<string, string> = {
  [TempAccessStatus.Pending]: "yellow",
  [TempAccessStatus.Active]: "green",
  [TempAccessStatus.Expired]: "gray",
  [TempAccessStatus.Revoked]: "red",
};

type SortAccessor = "userEmail" | "orgName" | "role" | "status" | "requestedAt" | "expiresAt";

interface GrantsTableProps {
  records: TempAccessGrant[];
  fetching: boolean;
  error: boolean;
  onRetry: () => void;
  showUser?: boolean;
  emptyText: string;
  actions?: (grant: TempAccessGrant) => ReactNode;
}

function compare(a: string | null, b: string | null): number {
  if (a === b) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return a.localeCompare(b, undefined, { sensitivity: "base" });
}

export function GrantsTable({
  records,
  fetching,
  error,
  onRetry,
  showUser = false,
  emptyText,
  actions,
}: GrantsTableProps) {
  const { t, i18n } = useTranslation("temp-access");
  const [sortStatus, setSortStatus] = useState<DataTableSortStatus<TempAccessGrant>>({
    columnAccessor: "requestedAt",
    direction: "desc",
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);

  const sorted = useMemo(() => {
    const accessor = sortStatus.columnAccessor as SortAccessor;
    const dir = sortStatus.direction === "asc" ? 1 : -1;
    return [...records].sort((a, b) => compare(a[accessor], b[accessor]) * dir);
  }, [records, sortStatus]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * pageSize;
  const paged = sorted.slice(start, start + pageSize);

  if (error) {
    return <ErrorState message={t("errors.listFailed")} onRetry={onRetry} />;
  }

  return (
    <Card withBorder padding={0}>
      <DataTable<TempAccessGrant>
        minHeight={250}
        withTableBorder={false}
        borderRadius="sm"
        highlightOnHover
        striped
        fetching={fetching}
        noRecordsText={emptyText}
        records={paged}
        idAccessor="id"
        totalRecords={sorted.length}
        recordsPerPage={pageSize}
        page={safePage}
        onPageChange={setPage}
        recordsPerPageOptions={PAGE_SIZE_OPTIONS}
        onRecordsPerPageChange={setPageSize}
        sortStatus={sortStatus}
        onSortStatusChange={setSortStatus}
        columns={[
          ...(showUser
            ? [
                {
                  accessor: "userEmail",
                  title: t("table.user"),
                  sortable: true,
                  render: (g: TempAccessGrant) => <Text size="sm">{g.userEmail}</Text>,
                },
              ]
            : []),
          { accessor: "orgName", title: t("table.organization"), sortable: true },
          {
            accessor: "role",
            title: t("table.role"),
            sortable: true,
            render: (g: TempAccessGrant) => <Badge variant="light">{g.role}</Badge>,
          },
          {
            accessor: "status",
            title: t("table.status"),
            sortable: true,
            render: (g: TempAccessGrant) => (
              <Badge color={STATUS_COLOR[g.status] ?? "gray"} variant="light">
                {t(`status.${g.status}`)}
              </Badge>
            ),
          },
          {
            accessor: "requestedAt",
            title: t("table.requested"),
            sortable: true,
            render: (g: TempAccessGrant) => (
              <Group gap="xs" wrap="nowrap">
                <Tooltip label={formatDateTime(g.requestedAt, i18n.language)}>
                  <Text size="sm">{formatRelative(g.requestedAt, i18n.language)}</Text>
                </Tooltip>
                <HoverCard width={280} shadow="md" withArrow position="top" openDelay={100}>
                  <HoverCard.Target>
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      size="sm"
                      aria-label={t("table.reason")}
                    >
                      <FiMessageSquare aria-hidden="true" />
                    </ActionIcon>
                  </HoverCard.Target>
                  <HoverCard.Dropdown>
                    <Text size="xs" fw={600} mb={4}>
                      {t("table.reason")}
                    </Text>
                    <Text size="sm">{g.reason}</Text>
                  </HoverCard.Dropdown>
                </HoverCard>
              </Group>
            ),
          },
          {
            accessor: "expiresAt",
            title: t("table.expires"),
            sortable: true,
            render: (g: TempAccessGrant) =>
              g.expiresAt ? (
                <Tooltip label={formatDateTime(g.expiresAt, i18n.language)}>
                  <Text size="sm">{formatRelative(g.expiresAt, i18n.language)}</Text>
                </Tooltip>
              ) : (
                <Text size="sm" c="dimmed">
                  —
                </Text>
              ),
          },
          ...(actions
            ? [
                {
                  accessor: "actions",
                  title: t("table.actions"),
                  textAlign: "right" as const,
                  render: (g: TempAccessGrant) => actions(g),
                },
              ]
            : []),
        ]}
      />
    </Card>
  );
}
